import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import SafeLogo from './SafeLogo';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { brand, navigation, cta } = useTheme();
  const { variant, styles } = useVariant();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const isActive = (path) => {
    return location.pathname === path;
  };

  // Get variant-specific navbar styling
  const getNavbarStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          nav: scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-gray-900 bg-gray-100',
            inactive: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          },
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          mobile: 'bg-white/95 backdrop-blur-md border-t border-gray-200'
        };
      case 'modern':
        return {
          nav: scrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-white/10' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-purple-200 bg-white/20',
            inactive: 'text-purple-100 hover:text-white hover:bg-white/10'
          },
          button: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white',
          mobile: 'bg-slate-900/95 backdrop-blur-md'
        };
      case 'corporate':
        return {
          nav: scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-primary bg-primary/10',
            inactive: 'text-gray-700 hover:text-primary hover:bg-primary/5'
          },
          button: 'bg-primary text-white hover:bg-primary/90',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
      case 'creative':
        return {
          nav: scrolled ? 'bg-white/90 backdrop-blur-md shadow-lg' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-purple-600 bg-purple-50',
            inactive: 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
          },
          button: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
      case 'dark':
        return {
          nav: scrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent',
          logo: scrolled ? 'bg-gray-900' : 'bg-gray-900/90 backdrop-blur-sm',
          link: {
            active: 'text-white bg-gray-800',
            inactive: 'text-gray-300 hover:text-white hover:bg-gray-800'
          },
          button: 'bg-white text-gray-900 hover:bg-gray-100',
          mobile: 'bg-gray-900/95 backdrop-blur-md'
        };
      case 'elegant':
        return {
          nav: scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-blue-800 bg-blue-100',
            inactive: 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
          },
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
      case 'professional':
        return {
          nav: scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-blue-600 bg-blue-50',
            inactive: 'text-slate-700 hover:text-blue-600 hover:bg-blue-50'
          },
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
      case 'clean':
        return {
          nav: scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-gray-900 bg-gray-100',
            inactive: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          },
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
      case 'grid':
        return {
          nav: scrolled ? 'backdrop-blur-md shadow-md border-b' : '',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-white bg-white/20',
            inactive: 'text-blue-100 hover:text-white hover:bg-white/10'
          },
          button: 'bg-white hover:bg-blue-50',
          buttonText: 'text-[#195A8E]',
          mobile: 'backdrop-blur-md',
          customStyle: { backgroundColor: '#195A8E' }
        };
      case 'magazine':
        return {
          nav: scrolled ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-gray-200' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-gray-800 bg-gray-100',
            inactive: 'text-gray-700 hover:text-gray-800 hover:bg-gray-50'
          },
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
      case 'landing':
        return {
          nav: scrolled ? 'bg-blue-600/95 backdrop-blur-md shadow-lg border-b border-white/20' : 'bg-transparent',
          logo: scrolled ? 'bg-transparent' : 'bg-transparent',
          link: {
            active: 'text-white bg-white/20',
            inactive: 'text-white/90 hover:text-white hover:bg-white/10'
          },
          button: 'bg-white text-blue-700 hover:bg-blue-50 font-semibold shadow-lg',
          mobile: 'bg-blue-600/95 backdrop-blur-md'
        };
      default:
        return {
          nav: scrolled ? 'bg-white/90 backdrop-blur-md shadow-md' : 'bg-transparent',
          logo: scrolled ? 'bg-white' : 'bg-white/90 backdrop-blur-sm',
          link: {
            active: 'text-primary bg-primary/5',
            inactive: 'text-gray-700 hover:text-primary hover:bg-primary/5'
          },
          button: 'bg-primary text-white hover:bg-primary/90',
          mobile: 'bg-white/95 backdrop-blur-md'
        };
    }
  };

  const navbarStyles = getNavbarStyles();

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${navbarStyles.nav}`}
      style={variant === 'grid' ? navbarStyles.customStyle : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <SafeLogo 
                context="navbar"
                className={`${variant === 'landing' ? 'h-16 w-auto rounded-lg' : 'h-12 w-auto'} ${(variant === 'landing' || variant === 'professional') ? '' : 'rounded-lg px-2 py-2'} transition-all duration-200 ${navbarStyles.logo}`}
                alt={brand.name}
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 mx-1 ${
                  isActive(item.path)
                    ? navbarStyles.link.active
                    : navbarStyles.link.inactive
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/request-demo"
              className={`ml-4 px-5 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${navbarStyles.button} ${navbarStyles.buttonText || ''}`}
            >
              {cta.primary}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none transition-all duration-200 ${
                scrolled 
                  ? 'text-gray-700 hover:text-primary hover:bg-gray-100' 
                  : 'text-gray-700 hover:text-primary hover:bg-white/20 bg-white/10 backdrop-blur-sm'
              }`}
              aria-expanded="false"
            >
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className={`md:hidden ${navbarStyles.mobile} transition-all duration-300 ease-in-out transform opacity-100 scale-100`}>
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? navbarStyles.link.active
                      : navbarStyles.link.inactive
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/request-demo"
                className={`block mt-3 px-4 py-2.5 rounded-md text-base font-medium transition-all duration-200 ${navbarStyles.button} ${navbarStyles.buttonText || ''}`}
                onClick={() => setIsOpen(false)}
              >
                {cta.primary}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 